import React, { useState, useEffect } from 'react';

// Amount still to allocate in an exact split (negative when over-allocated).
export const getRemainingAmount = (amount, selectedParticipants, exactAmounts) => {
  const totalAmount = parseFloat(amount) || 0;
  const exactSum = selectedParticipants.reduce((sum, id) => {
    const val = parseFloat(exactAmounts[id]);
    return sum + (isNaN(val) ? 0 : val);
  }, 0);
  return parseFloat((totalAmount - exactSum).toFixed(2));
};

// Split mechanism (equal / exact) plus the participant picker. State lives in the parent.
function SplitEditor({
  users,
  currentUser,
  amount,
  splitType,
  onSplitTypeChange,
  selectedParticipants,
  onSelectedParticipantsChange,
  exactAmounts,
  onExactAmountsChange,
}) {
  const [participantQuery, setParticipantQuery] = useState('');

  // Reset search query when the available user list changes
  useEffect(() => {
    setParticipantQuery('');
  }, [users]);

  const addParticipant = (userId) => {
    if (!selectedParticipants.includes(userId)) {
      onSelectedParticipantsChange([...selectedParticipants, userId]);
    }
  };

  const removeParticipant = (userId) => {
    onSelectedParticipantsChange(selectedParticipants.filter((id) => id !== userId));
  };

  const displayName = (u) => (u.id === currentUser.id ? 'You' : u.name);

  // Users that are not yet selected, filtered by the search query (case-insensitive on name).
  // Also lets the current user be found by typing "you" / "me".
  const unselectedFiltered = users.filter((u) => {
    if (selectedParticipants.includes(u.id)) return false;
    const q = participantQuery.trim().toLowerCase();
    if (!q) return false;
    const nameHaystack = [u.name, u.id === currentUser.id ? 'you' : '', u.id === currentUser.id ? 'me' : '']
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return nameHaystack.includes(q);
  });

  // Resolve the message shown in the results pane.
  const resultsMessage =
    participantQuery.trim() === ''
      ? 'Type to search…'
      : unselectedFiltered.length === 0
        ? 'No matching users.'
        : null;

  const remainingAmount = getRemainingAmount(amount, selectedParticipants, exactAmounts);
  const isRemainingZero = Math.abs(remainingAmount) < 0.01;

  return (
    <>
      <div className="form-group">
        <label>Split Mechanism</label>
        <div className="tab-buttons">
          <button
            type="button"
            className={`tab-btn ${splitType === 'equal' ? 'active' : ''}`}
            onClick={() => onSplitTypeChange('equal')}
          >
            Split Equally
          </button>
          <button
            type="button"
            className={`tab-btn ${splitType === 'exact' ? 'active' : ''}`}
            onClick={() => onSplitTypeChange('exact')}
          >
            Split Exactly
          </button>
        </div>
      </div>

      <div className="form-group">
        <label>Select Participants</label>

        {splitType === 'exact' && (
          <div className={`remaining-amount-banner ${isRemainingZero ? 'zero' : 'mismatch'}`}>
            {isRemainingZero ? (
              <span>✓ Split is fully allocated! (Remaining: ₹0.00)</span>
            ) : remainingAmount < 0 ? (
              <span>
                ⚠️ Over-allocated by: <strong>₹{Math.abs(remainingAmount).toFixed(2)}</strong>
              </span>
            ) : (
              <span>
                ⚠️ Remaining amount to split: <strong>₹{remainingAmount.toFixed(2)}</strong>
              </span>
            )}
          </div>
        )}

        {/* Selected chips */}
        <div className="participant-chips">
          {selectedParticipants.length === 0 ? (
            <p className="participant-empty-hint">
              No participants yet — search below to add people.
            </p>
          ) : (
            selectedParticipants.map((id) => {
              const u = users.find((x) => x.id === id);
              if (!u) return null;
              const computedShare =
                splitType === 'equal' && amount
                  ? (parseFloat(amount) / selectedParticipants.length).toFixed(2)
                  : null;
              return (
                <div key={id} className="participant-chip">
                  <span className="participant-chip-name">{displayName(u)}</span>

                  {splitType === 'equal' && computedShare && (
                    <span className="computed-share-text">₹{computedShare}</span>
                  )}

                  {splitType === 'exact' && (
                    <input
                      type="number"
                      step="0.01"
                      placeholder="Amount"
                      className="exact-split-input"
                      value={exactAmounts[id] || ''}
                      onChange={(e) => onExactAmountsChange({ ...exactAmounts, [id]: e.target.value })}
                      required
                    />
                  )}

                  <button
                    type="button"
                    className="participant-chip-remove"
                    aria-label={`Remove ${displayName(u)}`}
                    onClick={() => removeParticipant(id)}
                  >
                    ✕
                  </button>
                </div>
              );
            })
          )}
        </div>

        {/* Search input */}
        <input
          type="text"
          className="participant-search-input"
          placeholder="Search users to add…"
          value={participantQuery}
          onChange={(e) => setParticipantQuery(e.target.value)}
        />

        {/* Results list */}
        <div className="participant-results">
          {resultsMessage !== null ? (
            <p className="participant-empty-hint">{resultsMessage}</p>
          ) : (
            unselectedFiltered.map((u) => (
              <div
                key={u.id}
                className="participant-result-row"
                role="button"
                tabIndex={0}
                onClick={() => addParticipant(u.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    addParticipant(u.id);
                  }
                }}
              >
                <span>{displayName(u)}</span>
                <span className="participant-add-icon">+</span>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
}

export default SplitEditor;
